import React, { useMemo } from "react";
import TeamLogo from "../../common/TeamLogo";
import MatchLiveScorePanel from "../../common/MatchLiveScorePanel";
import MatchStatus from "../../lib/DateLive";
import GameDateDisplay from "../../lib/GameDateDisplay";
import { useBetSlipOptional } from "../../../context/BetSlipProvider";
import { getPreferredBookmaker } from "../../lib/oddsGames";
import { buildSelectionCheckId, findMarketInGame } from "../../../utility/betSlipSelection";
import { getWagerableOutcomeIndices } from "../../../utility/openBetBoardGames";
import {
  getActiveBetMatchView,
  isPlacedBetOutcome,
} from "../../../utility/activeBetMatchMarkets";
import { getDrawDisplayLabel } from "../../../utility/h2hOutcomes";
import { formatOdds, formatPoint, formatTotalPoint } from "../../../utility/odds";
import "../staightPage/staightPage.css";
import "./openbetExpand.css";

function WagerableOddsCheckbox({
  game,
  marketKey,
  outcomeIndex,
  variant = "spread",
  drawLabel = null,
}) {
  const betSlip = useBetSlipOptional();
  const found = findMarketInGame(game, marketKey);
  const market = found?.market;
  const outcome = market?.outcomes?.[outcomeIndex];

  if (!outcome || !game?.id || !market || !betSlip) {
    return <p>-</p>;
  }

  const checkId = buildSelectionCheckId(
    game.id,
    found.bookmakerIndex,
    found.marketIndex,
    outcomeIndex
  );
  const isChecked = betSlip.isSelectionChecked(checkId, game.id);

  const onChange = (e) => {
    betSlip.handleGameSelection(e, game, marketKey, outcomeIndex);
  };

  if (variant === "h2h") {
    return (
      <label>
        <input type="checkbox" checked={isChecked} onChange={onChange} />
        {drawLabel ? <h3 className="fs13">{drawLabel}</h3> : null}
        <h3 className={drawLabel ? "fs15" : "fs13"}>{formatOdds(outcome.price)}</h3>
      </label>
    );
  }

  if (variant === "total") {
    return (
      <label>
        <input type="checkbox" checked={isChecked} onChange={onChange} />
        <h3 className="fs13">
          {formatTotalPoint(outcome.point, outcome.name)}
        </h3>
        <p className="fs15">{formatOdds(outcome.price)}</p>
      </label>
    );
  }

  return (
    <label>
      <input type="checkbox" checked={isChecked} onChange={onChange} />
      <h3 className="fs13">{formatPoint(outcome.point)}</h3>
      <p className="fs15">{formatOdds(outcome.price)}</p>
    </label>
  );
}

function ReadOnlyOddsCell({ cell, variant = "spread" }) {
  const placed = cell?.isPlacedBet;
  const className = [
    "openbet-expand-odds-cell",
    placed ? "openbet-expand-odds-cell--placed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!cell || cell.display === "-") {
    return <p>-</p>;
  }

  if (variant === "h2h") {
    return (
      <div className={className}>
        {cell.label ? (
          <span className="openbet-expand-odds-point">{cell.label}</span>
        ) : null}
        <span className="openbet-expand-odds-price">{cell.display}</span>
      </div>
    );
  }

  if (variant === "total") {
    return (
      <div className={className}>
        <span className="openbet-expand-odds-point">
          {cell.pointDisplay ?? formatTotalPoint(cell.point, cell.label)}
        </span>
        <span className="openbet-expand-odds-price">{cell.display}</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <span className="openbet-expand-odds-point">
        {cell.pointDisplay ?? formatPoint(cell.point)}
      </span>
      <span className="openbet-expand-odds-price">{cell.display}</span>
    </div>
  );
}

function WagerableLegBoard({ legBet, game, view, legIndex, totalLegs, betType }) {
  const betSlip = useBetSlipOptional();
  const wagerable = Boolean(game && getPreferredBookmaker(game.bookmakers || []));
  const indices = useMemo(
    () => getWagerableOutcomeIndices(game, view.homeTeam, view.awayTeam),
    [game, view.homeTeam, view.awayTeam]
  );
  const showLegLabel = totalLegs > 1;

  const renderCell = (marketKey, outcomeIndex, variant, cell, drawLabel = null) => {
    if (wagerable && betSlip && Number.isInteger(outcomeIndex) && outcomeIndex >= 0) {
      return (
        <WagerableOddsCheckbox
          game={game}
          marketKey={marketKey}
          outcomeIndex={outcomeIndex}
          variant={variant}
          drawLabel={drawLabel}
        />
      );
    }
    return <ReadOnlyOddsCell cell={cell} variant={variant} />;
  };

  const homeRow = view.rows[0];
  const drawRow = view.rows.length === 3 ? view.rows[1] : null;
  const awayRow = view.rows[view.rows.length - 1];
  const h2hMarket = findMarketInGame(game, "h2h")?.market;
  const drawOutcome =
    drawRow && h2hMarket
      ? h2hMarket.outcomes?.[indices?.draw?.h2h]
      : null;
  const drawLabel =
    drawOutcome && isPlacedBetOutcome(legBet, "h2h", drawOutcome)
      ? getDrawDisplayLabel(drawOutcome.name)
      : drawOutcome
        ? getDrawDisplayLabel(drawOutcome.name)
        : null;

  return (
    <div className="openbet-expand-leg">
      {showLegLabel ? (
        <div className="openbet-expand-leg-label">
          Leg {legIndex + 1}
          {view.sportTitle ? ` · ${view.sportTitle}` : ""}
        </div>
      ) : null}

      <div className="game-card-item">
        <div className="bodyDetailsPart">
          <div className="detailsMiddlePart">
            <div className="content1">
              <div className="headingContent d-flex align-items-center gap-3">
                <TeamLogo
                  teamName={view.homeTeam}
                  logoUrl={game?.home_team_logo}
                  size="md"
                  alt={view.homeTeam}
                />
                <p className="fs15 teamNameText">{view.homeTeam}</p>
              </div>
              <p className="fs14 py-1 px-2">vs</p>
              <div className="headingContent d-flex align-items-center gap-3">
                <TeamLogo
                  teamName={view.awayTeam}
                  logoUrl={game?.away_team_logo}
                  size="md"
                  alt={view.awayTeam}
                />
                <p className="fs15 teamNameText">{view.awayTeam}</p>
              </div>
            </div>

            <div className="content2">
              <div className="openbet-expand-status-row">
                <MatchStatus matchTime={view.commenceTime} />
              </div>
              <div className="tvBgImg">
                <MatchLiveScorePanel game={game} phase={view.phase} />
              </div>
            </div>

            <div className="content3">
              <div className="headingTextPart">
                <ul className="d-flex align-items-center justify-content-between gap-2 m-0">
                  <li className="fs13">Spread</li>
                  <li className="fs13">M-Line</li>
                  <li className="fs13">Total</li>
                </ul>
              </div>

              <ul className="pointUlTag">
                <li>
                  {renderCell(
                    "spreads",
                    indices?.home?.spreads,
                    "spread",
                    homeRow?.spreads
                  )}
                </li>
                <li className="inputCheckboxWrapper">
                  {renderCell("h2h", indices?.home?.h2h, "h2h", homeRow?.h2h)}
                </li>
                <li>
                  {renderCell(
                    "totals",
                    indices?.home?.totals,
                    "total",
                    homeRow?.totals
                  )}
                </li>
              </ul>

              {drawRow ? (
                <ul className="pointUlTag pointUlTag--draw">
                  <li>
                    <ReadOnlyOddsCell cell={drawRow.spreads} variant="spread" />
                  </li>
                  <li className="inputCheckboxWrapper">
                    {renderCell(
                      "h2h",
                      indices?.draw?.h2h,
                      "h2h",
                      drawRow.h2h,
                      drawLabel
                    )}
                  </li>
                  <li>
                    <ReadOnlyOddsCell cell={drawRow.totals} variant="total" />
                  </li>
                </ul>
              ) : null}

              <ul className="pointUlTag">
                <li>
                  {renderCell(
                    "spreads",
                    indices?.away?.spreads,
                    "spread",
                    awayRow?.spreads
                  )}
                </li>
                <li className="inputCheckboxWrapper">
                  {renderCell("h2h", indices?.away?.h2h, "h2h", awayRow?.h2h)}
                </li>
                <li>
                  {renderCell(
                    "totals",
                    indices?.away?.totals,
                    "total",
                    awayRow?.totals
                  )}
                </li>
              </ul>
            </div>

            <div className="dateCardTemplate">
              <GameDateDisplay dateTime={view.commenceTime} />
            </div>
          </div>
        </div>
      </div>

      <div className="openbet-expand-placed-summary">
        <span className="openbet-expand-placed-summary__label">Your bet:</span>
        <span>{formatPlacedSelection(view, legBet, betType)}</span>
      </div>
    </div>
  );
}

function formatPlacedSelection(view, legBet, betType) {
  const { teamName, point, price, oddType, pointType } = view.placedSelection;
  const parts = [teamName];

  if (
    betType === "Teaser" &&
    legBet?.originalPoint != null &&
    legBet?.originalPoint !== ""
  ) {
    const formatLine = (value) =>
      legBet?.oddType === "totals"
        ? formatTotalPoint(value, legBet.pointType || legBet.teamName)
        : formatPoint(value);
    parts.push(
      `@ ${formatLine(legBet.point)} (orig ${formatLine(legBet.originalPoint)})`
    );
  } else if (point != null && point !== "") {
    const line =
      oddType === "totals"
        ? formatTotalPoint(point, pointType || teamName)
        : formatPoint(point);
    parts.push(`@ ${line}`);
  }

  parts.push(`(${formatOdds(price)})`);
  return parts.filter(Boolean).join(" ");
}

export default function WagerableMatchBoard({
  legBet,
  game,
  legIndex,
  totalLegs,
  betType,
}) {
  const view = getActiveBetMatchView(game, legBet);
  return (
    <WagerableLegBoard
      legBet={legBet}
      game={game}
      view={view}
      legIndex={legIndex}
      totalLegs={totalLegs}
      betType={betType}
    />
  );
}

export { ReadOnlyOddsCell, formatPlacedSelection };
